
import { useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';

const HomePage = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Local Business
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "BensonDesigns",
      "description": "Professional vehicle wraps, window tinting, and commercial signage services in Ridgeley, WV",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "+1-304-555-0123",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "242 McTaggart Estates Lane",
        "addressLocality": "Ridgeley",
        "addressRegion": "WV",
        "postalCode": "26753",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 39.6537,
        "longitude": -78.7736
      },
      "openingHours": "Mo-Fr 08:00-17:00",
      "priceRange": "$$",
      "areaServed": {
        "@type": "State",
        "name": "West Virginia"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 39.6537,
          "longitude": -78.7736
        },
        "geoRadius": "50000"
      }
    };

    // Add Schema.org JSON-LD for Services
    const servicesSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Vehicle Wraps and Window Tinting Services",
      "description": "Professional vehicle wraps, window tinting, and commercial signage",
      "provider": {
        "@type": "LocalBusiness",
        "name": "BensonDesigns"
      },
      "serviceType": ["Vehicle Wraps", "Window Tinting", "Commercial Signage"],
      "areaServed": {
        "@type": "State",
        "name": "West Virginia"
      }
    };

    // Add Schema.org JSON-LD for FAQ
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What types of vehicle wraps do you offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer full vehicle wraps, partial wraps, commercial fleet branding, and custom vehicle graphics for cars, trucks, vans, and commercial vehicles."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide window tinting services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we provide professional window tinting for both residential and commercial properties, as well as automotive window tinting with premium materials and lifetime warranty."
          }
        },
        {
          "@type": "Question",
          "name": "What is your service area?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We serve Ridgeley, WV and surrounding areas throughout West Virginia with our vehicle wrap, window tinting, and commercial signage services."
          }
        }
      ]
    };

    // Add all schemas to the page
    const addSchema = (schema: object, id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(localBusinessSchema, 'local-business-schema');
    addSchema(servicesSchema, 'services-schema');
    addSchema(faqSchema, 'faq-schema');

    // Cleanup function
    return () => {
      ['local-business-schema', 'services-schema', 'faq-schema'].forEach(id => {
        const script = document.getElementById(id);
        if (script) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <Hero />
        <Services />
        <WhyChooseUs />
        <Portfolio />
        <Testimonials />
        <Contact />
        <CallToAction />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
